import React from 'react';
import { colors } from '@dhis2/ui';
import { ClipboardCheckIcon, MessageCircleIcon } from 'lucide-react';
import { GiLinkedRings, GiOnTarget, GiProgression, GiWeight } from 'react-icons/gi';
import { GoIssueDraft } from 'react-icons/go';
import { useKraVisibility } from '../../organisationUsers/hooks/useKraVisibility';

const ObjectiveViewModal = ({ objective, index, onClose, goalItems, kraEnabled }) => {
  const { isKraEnabled: hookKraEnabled } = useKraVisibility();
  const effectiveKraEnabled = hookKraEnabled !== undefined ? hookKraEnabled : (kraEnabled !== undefined ? kraEnabled : true);

  if (!objective) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not specified';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const getObjectiveWeight = (obj) => {
    if (obj.weight && obj.weight > 0) return obj.weight;
    if (obj.dataValues) {
      const weightDV = obj.dataValues.find(
        dv => dv.dataElement === 'yfb9RCRpMB2' || dv.dataElement === 'lLCT66J73BQ'
      );
      if (weightDV?.value) {
        const parsed = parseInt(weightDV.value);
        if (!isNaN(parsed) && parsed > 0) return parsed;
      }
    }
    return 0;
  };

  const weight = getObjectiveWeight(objective);

  const getKRAs = (obj) => {
    if (obj.kras && Array.isArray(obj.kras) && obj.kras.length > 0) {
      return obj.kras.filter(k => k?.name?.trim());
    }
    if (obj.linkedKras && Array.isArray(obj.linkedKras) && obj.linkedKras.length > 0) {
      return obj.linkedKras
        .filter(k => k?.name?.trim())
        .map(kra => ({
          ...kra,
          weight: typeof kra.weight === 'string' ? parseInt(kra.weight) || 0 : kra.weight
        }));
    }
    if (obj.dataValues && Array.isArray(obj.dataValues)) {
      const nameDV = obj.dataValues.find(dv => dv.dataElement === 'obapz6Gnyla');
      if (nameDV?.value) {
        const weightDV = obj.dataValues.find(dv => dv.dataElement === 'Xl4VEFoIFIw');
        return [{
          name: nameDV.value,
          description: obj.dataValues.find(dv => dv.dataElement === 'DEECWPTj42E')?.value || '',
          weight: parseInt(weightDV?.value) || 0
        }];
      }
    }
    return [];
  };

  const getKPIs = (obj) => {
    if (obj.kpis && Array.isArray(obj.kpis) && obj.kpis.length > 0) {
      return obj.kpis.filter(k => k?.name?.trim());
    }
    if (obj.linkedKpis && Array.isArray(obj.linkedKpis) && obj.linkedKpis.length > 0) {
      return obj.linkedKpis.filter(k => k?.name?.trim());
    }
    return [];
  };

  const kras = effectiveKraEnabled ? getKRAs(objective) : [];
  const kpis = getKPIs(objective);

  // ✅ Resolve names once, use everywhere
  const alignedManagerName = objective.higherLevelObjectiveName
    || objective.higherLevelObjective?.name
    || (objective.objectiveAlignment ? `Objective ${objective.objectiveAlignment.substring(0, 8)}...` : null);

  const linkedGoalName = objective.linkedGoalName
    || goalItems?.find(g => g.id === objective.companyAlignedGoal)?.name
    || null;

  const isDraft = String(objective.status || '').toLowerCase() === 'draft';

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          maxWidth: '800px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
        }}
      >
        {/* ===== HEADER ===== */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: colors.blue100
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
            <div
              style={{
                width: '32px',
                height: '32px',
                backgroundColor: colors.blue600,
                color: 'white',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                fontSize: '0.9rem',
                flexShrink: 0
              }}
            >
              {index + 1}
            </div>
            <h3
              style={{
                margin: 0,
                color: colors.blue900,
                fontSize: '1.1rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
              title={objective.name || `Objective ${index + 1}`}
            >
              {objective.name || `Objective ${index + 1}`}
            </h3>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: colors.grey700,
              padding: '4px 8px',
              borderRadius: '4px',
              flexShrink: 0
            }}
            title="Close"
          >
            &times;
          </button>
        </div>

        {/* ===== SCROLLABLE CONTENT ===== */}
        <div style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>

          {/* ─── STATUS BADGES ROW (Weight + Draft + Linked Goal) ─── */}
          <div
            style={{
              display: 'flex',
              gap: '8px',
              flexWrap: 'wrap',
              marginBottom: '20px',
              alignItems: 'center'
            }}
          >
            {weight > 0 && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 12px',
                  backgroundColor: colors.blue050,
                  border: `1px solid ${colors.blue300}`,
                  borderRadius: '20px',
                  fontSize: '0.85rem',
                  fontWeight: 500
                }}
              >
                <GiWeight size={16} style={{ color: colors.blue700 }} />
                <span style={{ color: colors.blue900 }}>Weight:</span>
                <strong style={{ color: colors.blue700 }}>{weight}%</strong>
              </span>
            )}

            {isDraft && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '6px 12px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  borderRadius: '20px',
                  fontSize: '0.85rem',
                  fontWeight: 500
                }}
              >
                <GoIssueDraft size={14} /> Draft
              </span>
            )}

            {linkedGoalName && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 12px',
                  backgroundColor: colors.green100,
                  border: `1px solid ${colors.green300}`,
                  borderRadius: '20px',
                  fontSize: '0.85rem',
                  fontWeight: 500,
                  color: colors.green800,
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
                title={linkedGoalName}
              >
                <GiLinkedRings size={14} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {linkedGoalName}
                </span>
              </span>
            )}
          </div>

          {/* ─── DESCRIPTION ─── */}
          {objective.description && (
            <div style={{ marginBottom: '20px' }}>
              <label
                style={{
                  display: 'block',
                  fontWeight: 600,
                  color: colors.grey800,
                  marginBottom: '6px',
                  fontSize: '0.9rem'
                }}
              >
                Description
              </label>
              <p
                style={{
                  margin: 0,
                  padding: '12px',
                  backgroundColor: colors.grey050,
                  borderRadius: '4px',
                  color: colors.grey900,
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap',
                  fontSize: '0.95rem'
                }}
              >
                {objective.description}
              </p>
            </div>
          )}

          {/* ─── DATES ─── */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '16px',
              marginBottom: '20px'
            }}
          >
            <div>
              <label
                style={{
                  display: 'block',
                  fontWeight: 600,
                  color: colors.grey800,
                  marginBottom: '6px',
                  fontSize: '0.9rem'
                }}
              >
                Start Date
              </label>
              <p
                style={{
                  margin: 0,
                  padding: '10px 12px',
                  backgroundColor: colors.grey050,
                  borderRadius: '4px',
                  fontSize: '0.95rem'
                }}
              >
                {formatDate(objective.startDate)}
              </p>
            </div>
            <div>
              <label
                style={{
                  display: 'block',
                  fontWeight: 600,
                  color: colors.grey800,
                  marginBottom: '6px',
                  fontSize: '0.9rem'
                }}
              >
                Completion Date
              </label>
              <p
                style={{
                  margin: 0,
                  padding: '10px 12px',
                  backgroundColor: colors.grey050,
                  borderRadius: '4px',
                  fontSize: '0.95rem'
                }}
              >
                {formatDate(objective.completionDate)}
              </p>
            </div>
          </div>

          {/* ─── ALIGNMENT SECTION (single consolidated block) ─── */}
          {(alignedManagerName || linkedGoalName) && (
            <div
              style={{
                marginBottom: '24px',
                padding: '12px 16px',
                backgroundColor: colors.blue050,
                border: `1px solid ${colors.blue200}`,
                borderRadius: '6px'
              }}
            >
              <div
                style={{
                  fontWeight: 600,
                  color: colors.grey800,
                  fontSize: '0.9rem',
                  marginBottom: '10px'
                }}
              >
                Alignment
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {alignedManagerName && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '8px 12px',
                      backgroundColor: 'white',
                      borderRadius: '4px',
                      borderLeft: `3px solid ${colors.purple600}`
                    }}
                  >
                    <span style={{ fontSize: '1.1rem' }}>⬆️</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: '0.75rem',
                          color: colors.grey600,
                          fontWeight: 600,
                          marginBottom: '2px'
                        }}
                      >
                        Aligned to Manager Objective
                      </div>
                      <div
                        style={{
                          color: colors.grey900,
                          fontSize: '0.9rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                        title={alignedManagerName}
                      >
                        {alignedManagerName}
                      </div>
                    </div>
                  </div>
                )}

                {linkedGoalName && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '8px 12px',
                      backgroundColor: 'white',
                      borderRadius: '4px',
                      borderLeft: `3px solid ${colors.green600}`
                    }}
                  >
                    <GiLinkedRings size={18} style={{ color: colors.green700 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: '0.75rem',
                          color: colors.grey600,
                          fontWeight: 600,
                          marginBottom: '2px'
                        }}
                      >
                        Linked to Company Goal
                      </div>
                      <div
                        style={{
                          color: colors.grey900,
                          fontSize: '0.9rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                        title={linkedGoalName}
                      >
                        {linkedGoalName}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ─── KRAs SECTION (conditional) ─── */}
          {effectiveKraEnabled && kras.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <h4
                style={{
                  margin: '0 0 12px 0',
                  color: colors.grey800,
                  fontSize: '0.95rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  paddingBottom: '8px',
                  borderBottom: `2px solid ${colors.grey200}`
                }}
              >
                <ClipboardCheckIcon size={18} />
                Key Performance Areas ({kras.length})
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {kras.map((kra, kraIndex) => (
                  <div
                    key={kraIndex}
                    style={{
                      padding: '12px',
                      backgroundColor: colors.grey050,
                      border: `1px solid ${colors.grey300}`,
                      borderRadius: '4px'
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: '8px',
                        marginBottom: kra.description ? '6px' : 0
                      }}
                    >
                      <strong style={{ color: colors.grey900, fontSize: '0.95rem' }}>
                        {kra.name}
                      </strong>
                      {kra.weight > 0 && (
                        <span
                          style={{
                            fontSize: '0.8rem',
                            padding: '2px 8px',
                            backgroundColor: colors.blue100,
                            color: colors.blue800,
                            borderRadius: '10px',
                            fontWeight: 500,
                            flexShrink: 0
                          }}
                        >
                          {kra.weight}%
                        </span>
                      )}
                    </div>
                    {kra.description && (
                      <p
                        style={{
                          margin: 0,
                          fontSize: '0.85rem',
                          color: colors.grey700,
                          lineHeight: 1.4
                        }}
                      >
                        {kra.description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ─── KPIs SECTION (always visible) ─── */}
          {kpis.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <h4
                style={{
                  margin: '0 0 12px 0',
                  color: colors.grey800,
                  fontSize: '0.95rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  paddingBottom: '8px',
                  borderBottom: `2px solid ${colors.grey200}`
                }}
              >
                <GiProgression size={18} />
                Key Performance Indicators ({kpis.length})
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {kpis.map((kpi, kpiIndex) => {
                  const kpiWeightNum = Number(kpi.weight);
                  const hasWeight = Number.isFinite(kpiWeightNum) && kpiWeightNum > 0;
                  return (
                    <div
                      key={kpiIndex}
                      style={{
                        padding: '12px',
                        backgroundColor: colors.grey050,
                        border: `1px solid ${colors.grey300}`,
                        borderRadius: '4px'
                      }}
                    >
                      {/* Name + Weight */}
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          gap: '8px',
                          marginBottom: '8px'
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: '0.7rem',
                              color: colors.grey600,
                              fontWeight: 600,
                              marginBottom: '2px'
                            }}
                          >
                            KPI Name
                          </div>
                          <div
                            style={{
                              color: colors.grey900,
                              fontSize: '0.95rem',
                              fontWeight: 600
                            }}
                          >
                            {kpi.name}
                          </div>
                        </div>
                        <span
                          style={{
                            fontSize: '0.8rem',
                            padding: '4px 10px',
                            backgroundColor: hasWeight ? colors.blue100 : colors.grey100,
                            color: hasWeight ? colors.blue800 : colors.grey700,
                            borderRadius: '10px',
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            flexShrink: 0
                          }}
                        >
                          {hasWeight ? `${kpiWeightNum}%` : 'No weight'}
                        </span>
                      </div>

                      {/* Description */}
                      {kpi.description && (
                        <div style={{ marginBottom: '8px' }}>
                          <div
                            style={{
                              fontSize: '0.7rem',
                              color: colors.grey600,
                              fontWeight: 600,
                              marginBottom: '2px'
                            }}
                          >
                            Description
                          </div>
                          <div
                            style={{
                              fontSize: '0.85rem',
                              color: colors.grey700,
                              lineHeight: 1.4
                            }}
                          >
                            {kpi.description}
                          </div>
                        </div>
                      )}

                      {/* Measure */}
                      <div>
                        <div
                          style={{
                            fontSize: '0.7rem',
                            color: colors.grey600,
                            fontWeight: 600,
                            marginBottom: '4px'
                          }}
                        >
                          Measure
                        </div>
                        {kpi.target ? (
                          <span
                            style={{
                              fontSize: '0.85rem',
                              padding: '4px 10px',
                              backgroundColor: colors.green100,
                              color: colors.green800,
                              borderRadius: '10px',
                              fontWeight: 500,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            <GiOnTarget size={14} /> {kpi.target}
                          </span>
                        ) : (
                          <span
                            style={{
                              fontSize: '0.85rem',
                              color: colors.grey500,
                              fontStyle: 'italic'
                            }}
                          >
                            Not set
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ─── EMPTY STATE ─── */}
          {((effectiveKraEnabled && kras.length === 0) || !effectiveKraEnabled) && kpis.length === 0 && (
            <div
              style={{
                padding: '24px',
                backgroundColor: colors.grey050,
                borderRadius: '4px',
                textAlign: 'center',
                color: colors.grey600,
                marginBottom: '20px',
                fontStyle: 'italic',
                fontSize: '0.9rem'
              }}
            >
              {effectiveKraEnabled
                ? 'No KPIs or KRAs defined for this objective.'
                : 'No KPIs defined for this objective.'}
            </div>
          )}

          {/* ─── REMARKS ─── */}
          {objective.remarks && (
            <div
              style={{
                padding: '12px',
                backgroundColor: colors.orange050,
                border: `1px solid ${colors.orange300}`,
                borderRadius: '4px'
              }}
            >
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontWeight: 600,
                  color: colors.orange800,
                  marginBottom: '8px',
                  fontSize: '0.9rem'
                }}
              >
                <MessageCircleIcon size={16} /> Manager's Remarks
                {objective.approvalStatus === 'returned' && (
                  <span
                    style={{
                      fontSize: '0.7rem',
                      padding: '2px 6px',
                      backgroundColor: colors.orange200,
                      color: colors.orange900,
                      borderRadius: '8px',
                      fontWeight: 500
                    }}
                  >
                    ⚠️ Returned
                  </span>
                )}
              </label>
              <p
                style={{
                  margin: 0,
                  padding: '10px',
                  backgroundColor: 'white',
                  borderRadius: '4px',
                  color: colors.grey900,
                  fontStyle: 'italic',
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap',
                  fontSize: '0.9rem'
                }}
              >
                {objective.remarks}
              </p>
            </div>
          )}
        </div>

        {/* ===== FOOTER ===== */}
        <div
          style={{
            padding: '12px 20px',
            borderTop: '1px solid #e0e0e0',
            backgroundColor: colors.grey050,
            display: 'flex',
            justifyContent: 'flex-end'
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: '8px 20px',
              backgroundColor: colors.blue600,
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 500,
              fontSize: '0.9rem'
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ObjectiveViewModal;